import React from "react";

interface MealPlanElementActionsProps {
    onRefresh?: () => void;
    onLike?: () => void;
    onDislike?: () => void;
    refreshTooltip?: string;
    likeTooltip?: string;
    dislikeTooltip?: string;
    size?: "small" | "medium" | "large";
    variant?: "default" | "subtle";
    isLiked?: boolean;
    isDisliked?: boolean;
    className?: string;
    refreshDisabled?: boolean;
}

export const MealPlanElementActions: React.FC<MealPlanElementActionsProps> = ({
    onRefresh,
    onLike,
    onDislike,
    refreshTooltip = "Refresh",
    likeTooltip = "Like",
    dislikeTooltip = "Dislike",
    size = "medium",
    variant = "default",
    isLiked = false,
    isDisliked = false,
    className = "",
    refreshDisabled = false,
}) => {
    const getSizeStyles = () => {
        switch (size) {
            case "small":
                return {
                    buttonSize: "32px",
                    fontSize: "16px",
                    gap: "6px",
                };
            case "large":
                return {
                    buttonSize: "44px",
                    fontSize: "20px",
                    gap: "10px",
                };
            case "medium":
            default:
                return {
                    buttonSize: "36px",
                    fontSize: "18px",
                    gap: "8px",
                };
        }
    };

    const getVariantStyles = () => {
        switch (variant) {
            case "subtle":
                return {
                    backgroundColor: "transparent",
                    border: "none",
                    hoverBackgroundColor: "#f3f4f6",
                };
            case "default":
            default:
                return {
                    backgroundColor: "#ffffff",
                    border: "1px solid #d1d5db",
                    hoverBackgroundColor: "#f9fafb",
                };
        }
    };

    const sizeStyles = getSizeStyles();
    const variantStyles = getVariantStyles();

    const containerStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        gap: sizeStyles.gap,
    };

    const buttonStyle: React.CSSProperties = {
        width: sizeStyles.buttonSize,
        height: sizeStyles.buttonSize,
        borderRadius: "6px",
        border: variantStyles.border,
        backgroundColor: variantStyles.backgroundColor,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: sizeStyles.fontSize,
        transition: "all 0.2s ease",
        outline: "none",
    };

    const refreshButtonStyle: React.CSSProperties = {
        ...buttonStyle,
        color: refreshDisabled ? "#9ca3af" : "#3b82f6",
        cursor: refreshDisabled ? "not-allowed" : "pointer",
        opacity: refreshDisabled ? 0.5 : 1,
    };

    const likeButtonStyle: React.CSSProperties = {
        ...buttonStyle,
        color: isLiked ? "#ffffff" : "#10b981",
        backgroundColor: isLiked ? "#10b981" : variantStyles.backgroundColor,
        borderColor: isLiked
            ? "#059669"
            : variant === "default"
            ? "#d1d5db"
            : "transparent",
        fontWeight: isLiked ? "bold" : "normal",
    };

    const dislikeButtonStyle: React.CSSProperties = {
        ...buttonStyle,
        color: isDisliked ? "#ffffff" : "#ef4444",
        backgroundColor: isDisliked ? "#ef4444" : variantStyles.backgroundColor,
        borderColor: isDisliked
            ? "#dc2626"
            : variant === "default"
            ? "#d1d5db"
            : "transparent",
        fontWeight: isDisliked ? "bold" : "normal",
    };

    const handleButtonHover = (e: React.MouseEvent<HTMLButtonElement>) => {
        const button = e.currentTarget;
        const isRefreshButton = button.title === refreshTooltip;
        
        // Don't apply hover effects if refresh is disabled
        if (isRefreshButton && refreshDisabled) {
            return;
        }
        
        const isLikeButton =
            button.title.toLowerCase().includes("like") &&
            !button.title.toLowerCase().includes("dislike");
        const isDislikeButton = button.title.toLowerCase().includes("dislike");

        if (isLikeButton && isLiked) {
            button.style.backgroundColor = "#059669"; // Darker green
        } else if (isDislikeButton && isDisliked) {
            button.style.backgroundColor = "#dc2626"; // Darker red
        } else {
            button.style.backgroundColor = variantStyles.hoverBackgroundColor;
        }

        if (
            variant === "default" &&
            !((isLikeButton && isLiked) || (isDislikeButton && isDisliked))
        ) {
            button.style.borderColor = "#9ca3af";
        }
        button.style.transform = "scale(1.05)";
    };

    const handleButtonLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
        const button = e.currentTarget;
        const isRefreshButton = button.title === refreshTooltip;
        
        // Don't apply leave effects if refresh is disabled
        if (isRefreshButton && refreshDisabled) {
            return;
        }
        
        const isLikeButton =
            button.title.toLowerCase().includes("like") &&
            !button.title.toLowerCase().includes("dislike");
        const isDislikeButton = button.title.toLowerCase().includes("dislike");

        if (isLikeButton && isLiked) {
            button.style.backgroundColor = "#10b981";
            button.style.borderColor = "#059669";
        } else if (isDislikeButton && isDisliked) {
            button.style.backgroundColor = "#ef4444";
            button.style.borderColor = "#dc2626";
        } else {
            button.style.backgroundColor = variantStyles.backgroundColor;
            if (variant === "default") {
                button.style.borderColor = "#d1d5db";
            }
        }
        button.style.transform = "scale(1)";
    };

    return (
        <div className={className} style={containerStyle}>
            {onRefresh && (
                <button
                    style={refreshButtonStyle}
                    onClick={refreshDisabled ? undefined : onRefresh}
                    title={refreshDisabled ? "Subscribe to use AI regeneration" : refreshTooltip}
                    onMouseEnter={handleButtonHover}
                    onMouseLeave={handleButtonLeave}
                    disabled={refreshDisabled}
                >
                    🔄
                </button>
            )}
            {onLike && (
                <button
                    style={likeButtonStyle}
                    onClick={onLike}
                    title={likeTooltip}
                    onMouseEnter={handleButtonHover}
                    onMouseLeave={handleButtonLeave}
                >
                    👍
                </button>
            )}
            {onDislike && (
                <button
                    style={dislikeButtonStyle}
                    onClick={onDislike}
                    title={dislikeTooltip}
                    onMouseEnter={handleButtonHover}
                    onMouseLeave={handleButtonLeave}
                >
                    👎
                </button>
            )}
        </div>
    );
};
